import { MemberReviewDetailResponse } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row, truncateString } from '@/lib/utils';
import React from 'react';
import NewProfileImage from '@/components/NewProfileImage';
import SvgIcon from '@/components/SvgIcon';
import { GradientStars } from '@/components/RatingStars/RatingStars';

type Props = {
    detail: MemberReviewDetailResponse;
};

function ModalMemberReviewHeader({ detail }: Props) {
    const genderLabel = detail.memberGender === 'male' ? '남성' : detail.memberGender === 'female' ? '여성' : '-';
    const ageLabel = detail.memberAge ?? '-';
    const membershipName = detail.membershipName ? truncateString(detail.membershipName, 22) : '-';
    const membershipProgress =
        detail.membershipSession !== undefined && detail.membershipTotalSession !== undefined
            ? `${detail.membershipSession}/${detail.membershipTotalSession}회차 완료`
            : '-';
    const membershipPeriod = detail.membershipPeriod ? `${detail.membershipPeriod}분` : '-';
    const membershipDday =
        detail.membershipDday !== undefined
            ? detail.membershipDday >= 0
                ? `D-${detail.membershipDday}`
                : `D+${Math.abs(detail.membershipDday)}`
            : '-';

    return (
        <Row className="items-center gap-[19px]">
            {/* 왼쪽: 회원 정보 */}
            <Row className="items-center">
                <NewProfileImage
                    size={56}
                    className="mr-[10px]"
                    imageUrl={detail.memberProfileImageUrl}
                    circleColor="#F2F4F6"
                    iconSize={25}
                />

                <Col className="gap-[10px]">
                    <Row className="flex items-center">
                        <MediumText fontSize={23} lineHeight={30} color={colors.Text_1} className="mr-[9px]">
                            {detail.memberAlias}
                        </MediumText>
                        <Row className="h-[32px] border border-solid border-[#E8E8E8] bg-[#F2F4F6] px-[10px] rounded-[50px] items-center justify-center">
                            <SvgIcon name="SvgUser" size={11} fill={'#444A51'} className="mr-[4px]" />
                            {/* 강사이름 */}
                            <RegularText fontSize={14} lineHeight={20} color={colors.Text_1}>
                                {detail.coachName}
                            </RegularText>
                        </Row>
                    </Row>

                    <Row className="items-center gap-[10px]">
                        <RegularText fontSize={14} lineHeight={19} color={colors.Text_1}>
                            {ageLabel}세
                        </RegularText>
                        <div className="w-[0.75px] h-[12px] bg-Text_1" />
                        <RegularText fontSize={14} lineHeight={19} color={colors.Text_1}>
                            {genderLabel}
                        </RegularText>
                        <div className="w-[0.75px] h-[12px] bg-Text_1" />
                        <Row className="items-center">
                            <RegularText fontSize={14} lineHeight={19} color={colors.Text_1} className="mr-[5px]">
                                전체평균만족도
                            </RegularText>
                            <GradientStars
                                rating={detail.memberTotalReviewAverageRating}
                                size={14}
                                gap={-2}
                                showOnlyFilled
                            />

                            <MediumText fontSize={16} lineHeight={21} color={'#191F28'} className="ml-[8px]">
                                {detail.memberTotalReviewAverageRating.toFixed(1)}
                            </MediumText>
                        </Row>
                    </Row>
                </Col>
            </Row>

            {/* 오른쪽: 수강권 정보 */}
            <Row
                className="items-center px-[24px] h-[80px] rounded-[18px] border-[0.5px] border-solid border-[#C3D5F7]"
                style={{
                    background: 'linear-gradient(90deg, #EDEFF6 0%, #E8ECFE 50%, #D9E3F3 100%)',
                }}
            >
                {/* 왼쪽: 수강권명 + 프로그레스 바 */}
                <Row className="flex-1 gap-[19px]">
                    <Col className="flex ">
                        <MediumText
                            fontSize={15}
                            lineHeight={21}
                            color={'#324361'}
                            letterSpacing={-0.7}
                            className="mb-[20px]"
                        >
                            {truncateString(membershipName, 20)}
                        </MediumText>
                        {/* 프로그레스 바 */}
                        <div className="w-full h-[5px] bg-white rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#697AA1] rounded-full transition-all"
                                style={{
                                    width: `${Math.min(
                                        100,
                                        (parseInt(membershipProgress.split('/')[0]) /
                                            parseInt(membershipProgress.split('/')[1])) *
                                            100,
                                    )}%`,
                                }}
                            />
                        </div>
                    </Col>
                    <Col className=" gap-[9px] items-start">
                        <Row className=" items-center">
                            <Col className=" justify-center items-center w-[15px] h-[15px] rounded-full bg-[#697AA1] mr-[4px]">
                                <SvgIcon
                                    name="SvgCheck"
                                    size={9}
                                    fill={colors.trans}
                                    stroke="white"
                                    strokeWidth={1.25}
                                />
                            </Col>
                            <RegularText fontSize={14} lineHeight={19} color={colors.Text_1} letterSpacing={-0.3}>
                                {membershipProgress}
                            </RegularText>
                        </Row>
                        {/* 오른쪽: 정보 태그들 */}
                        <Row className="items-center gap-[8px]">
                            <div className="px-[7px] py-[3px] bg-white rounded-[20px]">
                                <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.3}>
                                    {membershipPeriod}
                                </DemiLightText>
                            </div>
                            <div className="px-[7px] py-[3px] bg-white rounded-[20px]">
                                <DemiLightText fontSize={13} lineHeight={18} color={colors.Text_1} letterSpacing={-0.3}>
                                    {membershipDday}
                                </DemiLightText>
                            </div>
                        </Row>
                    </Col>
                </Row>
            </Row>
        </Row>
    );
}

export default ModalMemberReviewHeader;
